import React, { BaseSyntheticEvent, FC } from "react";
import { CheckLg } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import trialGirl from "./img/trial-girl.webp";
import trialGirlMobile from "./img/trial-girl-mobile.webp";
import "./ActiveTrialModal.scss";
import { usePaymentPlansQuery } from "../../../graphql/payment.hooks";
import { selectedCurrency } from "../../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import {
  findRenewalPlan,
  formatRenewalPrice,
} from "../../../services/subscriptionTrial";

type Props = {
  show: boolean;
  onCancel?: any;
  trialEndsAt?: string | null;
};

const ActiveTrialModal: FC<Props> = ({ show, onCancel, trialEndsAt }) => {
  const { t, i18n } = useTranslation();
  const currentCurrency = useReactiveVar(selectedCurrency);
  const { data: paymentPlansData } = usePaymentPlansQuery({
    variables: {
      locale: i18n.language?.split("-")[0] || "en",
      currency: currentCurrency,
    },
    skip: !show,
  });
  const trialEndDate = trialEndsAt
    ? new Intl.DateTimeFormat(i18n.language, {
        month: "long",
        day: "numeric",
      }).format(new Date(trialEndsAt))
    : null;
  const formattedTrialPrice = formatRenewalPrice(
    findRenewalPlan(paymentPlansData?.paymentPlans.plans),
    currentCurrency
  );

  const closeByBackground = (e: BaseSyntheticEvent) => {
    e.stopPropagation();
    if (e.target.id === "activeTrialModal") onCancel?.();
  };

  return (
    <div
      id="activeTrialModal"
      className={`modal ${show ? "show d-flex" : ""} align-items-center justify-content-center`}
      style={{ background: "rgb(0, 0, 0, 0.6)" }}
      onClick={(e) => closeByBackground(e)}
    >
      <div className="activeTrialModalDialog w-100 mx-3">
        <div className="activeTrialModalContent modal-content position-relative overflow-hidden border-0 rounded-4 bg-white text-break">
          <button
            type="button"
            className="activeTrialModalClose btn-close position-absolute z-2 p-0 opacity-75"
            aria-label="Close"
            onClick={onCancel}
          />

          <div className="activeTrialModalDetails position-relative z-1 d-flex flex-column pe-md-5">
            <div className="d-flex align-items-center gap-2">
              <img
                src={`${process.env.REACT_APP_ME_PAGE_URL}/images/logo.svg`}
                alt="me-page logo"
                className="d-block h-auto"
                width={76}
              />
              <span className="activeTrialModalBadge bg-peach-light text-secondary rounded-3 px-2 py-1">
                {t("modal.activeTrialBadge")}
              </span>
            </div>

            <h2 className="fs-3 fs-md-2 mt-3 mt-md-5 fw-bold text-dark mb-2">
              {t("modal.activeTrialTitle1")}
              <br />
              {t("modal.activeTrialTitle2")}
            </h2>

            <p className="fs-7 fs-md-6 text-gray mb-0">
              {t("modal.activeTrialAccessUntil")}{" "}
              <strong className="text-dark">
                {trialEndDate || t("modal.activeTrialFallbackDate")}.
              </strong>
              {formattedTrialPrice && (
                <>
                  <br />
                  {t("modal.activeTrialAutoRenew")}{" "}
                  <strong className="text-dark">
                    {`${formattedTrialPrice}/${t("payment.year")}.`}
                  </strong>
                </>
              )}
            </p>

            <ul className="mt-4 list-unstyled text-gray mb-4 mb-md-5">
              <li className="d-flex align-items-center gap-2 mb-2">
                <CheckLg className="fs-5 text-success" aria-hidden="true" />
                <span className="fs-7 fs-md-6">
                  <strong>500+</strong> {t("modal.activeTrialBenefit1Text")}
                </span>
              </li>
              <li className="d-flex align-items-center gap-2 mb-2">
                <CheckLg className="fs-5 text-success" aria-hidden="true" />
                <span className="fs-7 fs-md-6">
                  <strong>Advanced Mode</strong>{" "}
                  {t("modal.activeTrialBenefit2Text")}
                </span>
              </li>
              <li className="d-flex align-items-center gap-2">
                <CheckLg className="fs-5 text-success" aria-hidden="true" />
                <span className="fs-7 fs-md-6">
                  <strong>No Ads</strong> {t("modal.activeTrialBenefit3Text")}
                </span>
              </li>
            </ul>

            <button
              type="button"
              className="w-100 fs-6 btn btn-primary text-white fw-bold rounded-3"
              onClick={onCancel}
            >
              {t("modal.activeTrialCta")}
            </button>
          </div>

          <picture className="activeTrialModalPicture position-absolute end-0 bottom-0 z-0 d-flex align-items-end justify-content-end pe-none">
            <source media="(max-width: 768px)" srcSet={trialGirlMobile} />
            <img
              className="activeTrialModalArt d-block"
              src={trialGirl}
              alt="Premium page builder features"
            />
          </picture>
        </div>
      </div>
    </div>
  );
};

export default ActiveTrialModal;
